import { useQuery } from "@tanstack/react-query";
import { collection, getDocs, type QueryDocumentSnapshot, type DocumentData } from "firebase/firestore";
import { getDb } from "@repo/backend/firebase";
import { ResourceSchema, AvailabilityRuleSchema } from "../schema/booking.js";
import type { Resource, AvailabilityRule } from "../schema/booking.js";

export interface AvailabilityData {
  resources: Resource[];
  rules: AvailabilityRule[];
}

async function fetchAvailability(clientId: string): Promise<AvailabilityData> {
  const db = getDb();
  const [resourceSnap, ruleSnap] = await Promise.all([
    getDocs(collection(db, "clients", clientId, "resources")),
    getDocs(collection(db, "clients", clientId, "availability")),
  ]);

  const resources = resourceSnap.docs
    .map((d: QueryDocumentSnapshot<DocumentData>) => ResourceSchema.safeParse({ id: d.id, ...d.data() }))
    .filter((r): r is { success: true; data: Resource } => r.success && r.data.active)
    .map((r) => r.data);

  const rules = ruleSnap.docs
    .map((d: QueryDocumentSnapshot<DocumentData>) => AvailabilityRuleSchema.safeParse({ id: d.id, ...d.data() }))
    .filter((r): r is { success: true; data: AvailabilityRule } => r.success)
    .map((r) => r.data);

  return { resources, rules };
}

export function useAvailability(clientId: string) {
  return useQuery<AvailabilityData>({
    queryKey: ["client", clientId, "availability"],
    queryFn: () => fetchAvailability(clientId),
    staleTime: 10 * 60 * 1000, // 10 min — resources/rules change rarely
    enabled: !!clientId,
  });
}
